import { yupResolver } from "@hookform/resolvers/yup";
import { Autocomplete, Chip, DialogContent, TextField } from "@mui/material";
import { useCallback, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useApiServiceCallback } from "../../../../../server/api/client";
import { MomentService } from "../../../client";
import { isValidEmail, testSendMultiSchema } from "../../../types";
import { SendTestDialogButtons } from "./send-test-dialog-buttons";

import type { UUID } from "../../../../../utils/uuid";
import type { FC, ReactNode } from "react";

interface Props {
  id: UUID;
  setSaving: (value: boolean) => void;
  setTestDialog: (value: boolean) => void;
  publishing: boolean;
  children?: ReactNode;
}

export const SendTestToEmail: FC<Props> = ({
  id,
  setSaving,
  setTestDialog,
  publishing,
  children,
}) => {
  const [inputValue, setInputValue] = useState("");

  const send = useApiServiceCallback(
    MomentService,
    async ({ apiService }, emails: string[]) => await apiService.sendTest(id, emails, true),
    [id],
  );

  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
    setValue,
    getValues,
  } = useForm<{ emails: string[] }>({
    defaultValues: { emails: [] },
    resolver: yupResolver(testSendMultiSchema),
  });

  useWatch({ control, name: "emails" });
  const emails = getValues("emails");

  const hasInvalid = emails.some((e) => !isValidEmail(e));

  const commitInput = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    setValue("emails", [...emails, trimmed]);
    setInputValue("");
  }, [inputValue, emails, setValue]);

  const addChipsFromString = useCallback(
    (raw: string) => {
      const parts = raw
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean);
      if (parts.length === 0) return;
      setValue("emails", [...emails, ...parts]);
      setInputValue("");
    },
    [emails, setValue],
  );

  const onSubmit = useCallback(
    async ({ emails: submittedEmails }: { emails: string[] }): Promise<void> => {
      const { error } = await send(submittedEmails);
      setSaving(false);
      if (error) {
        toast.error("Unable to send test Communication, please try again.");
        console.error(error);
      } else {
        toast.success("Test Communication sent");
        setTestDialog(false);
      }
    },
    [send, setSaving, setTestDialog],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DialogContent>
        {children}
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={emails}
          inputValue={inputValue}
          onInputChange={(_, value) => setInputValue(value)}
          onChange={(_, newValue) => setValue("emails", newValue)}
          renderValue={(value, getTagProps) =>
            value.map((email, index) => (
              <Chip
                {...getTagProps({ index })}
                key={`${email}-${index}`}
                label={email}
                size="small"
                color={isValidEmail(email) ? "default" : "error"}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              label="Email address(es)"
              error={hasInvalid}
              helperText={hasInvalid ? "Fix invalid addresses before sending" : " "}
              sx={{ mt: 2 }}
              onKeyDown={(e) => {
                if (e.key === ",") {
                  e.preventDefault();
                  commitInput();
                } else if (e.key === "Tab" && inputValue.trim()) {
                  e.preventDefault();
                  commitInput();
                }
              }}
              onPaste={(e) => {
                e.preventDefault();
                addChipsFromString(e.clipboardData.getData("text"));
              }}
              slotProps={{
                htmlInput: {
                  ...params.inputProps,
                  autoComplete: "email",
                  onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                    (params.inputProps as React.InputHTMLAttributes<HTMLInputElement>).onBlur?.(e);
                    commitInput();
                  },
                },
              }}
            />
          )}
        />
      </DialogContent>
      <SendTestDialogButtons
        setSaving={setSaving}
        setTestDialog={setTestDialog}
        publishing={publishing}
        isSubmitting={isSubmitting}
        sendDisabled={emails.length === 0 || hasInvalid}
      />
    </form>
  );
};
